"use client";

/**
 * MobileStickyAudit — a sticky bottom "paste a URL" bar for mobile.
 *
 * Why: ~86% of traffic is mobile and average scroll depth is ~24%, so the
 * run-first action scrolls out of reach for anyone who skims. This keeps it in
 * thumb reach across the content pages without adding weight to any hero.
 *
 * Where it shows: every marketing page EXCEPT task/form pages (contact,
 * feedback, suggest-feature), the guest-audit result page (/try), and legal
 * pages — anywhere the user is mid-form or there's no audit intent. It's
 * mounted once in the marketing layout; the denylist below gates per route.
 *
 * Reveal:
 *   - On pages with an inline URL form (the homepage hero / final CTA), the
 *     bar stays hidden while any of those forms is on screen and slides up in
 *     the stretch between them.
 *   - On pages without an inline form (pricing, about, blog…), it slides up
 *     after a short scroll and tucks away again near the footer.
 *
 * It reuses <HeroUrlForm/> (identical submit behaviour) and stays above the
 * iOS keyboard via the VisualViewport API — the hard part of a sticky input
 * on iOS Safari (~42% of our traffic).
 */

import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { HeroUrlForm } from "./hero-url-form";

// Routes where the bar must NOT appear: task/form pages whose own inputs it
// would compete with, the guest-audit page, and low-intent legal pages.
// (/feedback and /suggest-feature were removed from this list when those
// routes became permanent redirects to /contact in next.config.mjs — the
// redirect fires before this component ever sees the path.)
const HIDDEN_PREFIXES = [
  "/contact",
  "/try",
  "/terms",
  "/privacy",
  "/sitemap",
];

function isHiddenPath(pathname: string | null): boolean {
  if (!pathname) return false;
  return HIDDEN_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

export function MobileStickyAudit() {
  const pathname = usePathname();
  const hidden = isHiddenPath(pathname);
  const [visible, setVisible] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);

  // Reveal logic — keyed on pathname so client-side nav between content pages
  // re-attaches to the new page's forms instead of watching stale elements.
  useEffect(() => {
    if (hidden) {
      setVisible(false);
      return;
    }

    const forms = Array.from(
      document.querySelectorAll<HTMLElement>(
        ".lp-hero .lp-hero-form, .lp-final-cta .lp-hero-form"
      )
    );

    // Pages with an inline form (homepage): hide while any is on screen, so
    // the bar only appears in the stretch between the hero and the final CTA.
    if (forms.length > 0) {
      const onScreen = new Set<Element>();
      const io = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) onScreen.add(entry.target);
            else onScreen.delete(entry.target);
          }
          setVisible(onScreen.size === 0);
        },
        { rootMargin: "0px 0px -10% 0px" }
      );
      forms.forEach((form) => io.observe(form));
      return () => io.disconnect();
    }

    // Pages without an inline form (pricing, about, blog…): reveal after a
    // short scroll, and tuck away near the footer so it never covers it.
    const onScroll = () => {
      const doc = document.documentElement;
      const nearBottom =
        window.scrollY + window.innerHeight >= doc.scrollHeight - 140;
      setVisible(window.scrollY > 320 && !nearBottom);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [hidden, pathname]);

  // Keep the bar above the on-screen keyboard (iOS Safari hides a fixed bottom
  // input behind the keyboard otherwise).
  useEffect(() => {
    if (hidden) return;
    const vv = window.visualViewport;
    const bar = barRef.current;
    if (!vv || !bar) return;

    const update = () => {
      const overlap = window.innerHeight - vv.height - vv.offsetTop;
      bar.style.setProperty("--msa-kb", `${Math.max(0, Math.round(overlap))}px`);
    };
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    update();
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
    };
  }, [hidden, pathname]);

  if (hidden) return null;

  return (
    <div
      ref={barRef}
      className="msa-bar"
      data-visible={visible}
      role="region"
      aria-label="Audit any website"
      aria-hidden={!visible}
    >
      <p className="msa-hook">
        {/* No icon. The magnifier read as "search box" next to an input,
            and its replacement (a pulse line) needed explaining — an icon
            that needs a legend is decoration. The question carries the
            line by itself. Sentence stays wrapped in one span so the
            flex gap can't split the trailing "?" off (bare text nodes
            count as flex items). */}
        <span className="msa-hook-text">
          What&rsquo;s hurting your site&rsquo;s{" "}
          <span className="msa-hook-accent">UX</span>?
        </span>
      </p>
      <HeroUrlForm
        buttonLabel="Audit my site"
        placeholder="yoursite.com"
        surface="sticky"
      />
      {/* Reassurance reveal — collapsed until the visitor focuses the
          field, then slides in to answer the three hesitations people
          have at exactly this moment: price, time, commitment. Shown at
          the commit moment rather than permanently so the resting bar
          stays two lines tall. */}
      {/* "No card", not "no signup" — the full report sits behind a free
          sign-in, so this line must not promise otherwise. */}
      <p className="msa-reassure">Free · about a minute · no card</p>
    </div>
  );
}
